import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Flame, Leaf, Truck, Heart, Star, HeartHandshake, Check, ArrowRight, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../api/api';
import SEOMeta from '../components/SEOMeta';

const iconMap = { Flame, Leaf, Truck, Heart, Star, HeartHandshake };

const fadeUp = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

export default function ServiziPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [form, setForm] = useState({ pet_name: '', pet_species: 'cane', contact_name: '', contact_email: '', contact_phone: '', preferred_date: '', address: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/services').then(r => setServices(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleBook = (service) => {
    setSelectedService(service);
    setBookingDialog(true);
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    if (!form.pet_name || !form.contact_name || !form.contact_email) {
      toast.error('Compila i campi obbligatori');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/bookings', { ...form, service_id: selectedService.id });
      toast.success('Richiesta inviata! Ti contatteremo presto.');
      setBookingDialog(false);
      setForm({ pet_name: '', pet_species: 'cane', contact_name: '', contact_email: '', contact_phone: '', preferred_date: '', address: '', notes: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'invio della richiesta');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[var(--color-cream)] flex items-center justify-center">
      <div className="spinner" />
    </div>
  );

  return (
    <div className="bg-[var(--color-cream)] min-h-screen">
      <SEOMeta
        title="I Nostri Servizi"
        description="Scopri i servizi di cremazione individuale, urne, cerimonie commemorative e supporto nel lutto offerti da Una Zampa in Paradiso. Cura, rispetto e dignità per il tuo animale."
        url="/servizi"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Cremazione Animali Domestici",
          "provider": { "@type": "LocalBusiness", "name": "Una Zampa in Paradiso" },
          "areaServed": "Italy"
        }}
      />
      {/* Header */}
      <section className="hero-gradient py-16 lg:py-24">
        <div className="container-custom text-center">
          <motion.div {...fadeUp}>
            <div className="inline-flex items-center gap-2 text-[var(--color-sage)] text-xs font-bold uppercase tracking-widest mb-4">
              <Flame size={14} /> Cremazione e assistenza
            </div>
            <h1 className="font-serif text-5xl font-semibold text-[var(--color-deep)] mb-4">{t('services.title')}</h1>
            <p className="text-[var(--color-muted-text)] max-w-2xl mx-auto text-lg">{t('services.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-py">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((s, i) => {
              const Icon = iconMap[s.icon] || Heart;
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`service-card-${s.id}`}
                >
                  {s.photo_url && (
                    <div className="h-48 overflow-hidden">
                      <img src={s.photo_url} alt={lang === 'en' ? s.name_en : s.name_it} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-[var(--color-sage-soft)] rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon size={22} className="text-[var(--color-sage)]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-serif font-semibold text-[var(--color-deep)] text-xl mb-1">
                          {lang === 'en' ? s.name_en : s.name_it}
                        </h3>
                        {s.price_info && (
                          <span className="text-xs font-medium text-[var(--color-sage)] bg-[var(--color-sage-soft)] px-3 py-1 rounded-full">
                            {s.price_info}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-[var(--color-muted-text)] leading-relaxed mb-5">
                      {lang === 'en' ? s.description_en : s.description_it}
                    </p>
                    {(lang === 'en' ? s.what_includes_en : s.what_includes_it)?.length > 0 && (
                      <div className="mb-5">
                        <p className="text-xs font-semibold text-[var(--color-muted-text)] uppercase tracking-wide mb-3">{t('services.includes')}</p>
                        <ul className="grid grid-cols-1 gap-2">
                          {(lang === 'en' ? s.what_includes_en : s.what_includes_it).map((item, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-[var(--color-ink)]">
                              <Check size={14} className="text-[var(--color-sage)] flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <Button
                      className="w-full bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl btn-press"
                      onClick={() => handleBook(s)}
                      data-testid={`service-book-button-${s.id}`}
                    >
                      <Calendar size={16} className="mr-2" /> {t('services.request')}
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Booking Dialog */}
      <Dialog open={bookingDialog} onOpenChange={setBookingDialog}>
        <DialogContent className="bg-[var(--color-paper)] max-w-lg border-[var(--color-border)]">
          <DialogHeader>
            <DialogTitle className="font-serif text-[var(--color-deep)]">
              {t('services.request')} - {selectedService && (lang === 'en' ? selectedService.name_en : selectedService.name_it)}
            </DialogTitle>
            <DialogDescription className="text-[var(--color-muted-text)]">
              Compila il modulo per inviare la tua richiesta. Ti contatteremo il prima possibile.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitBooking} className="space-y-4" data-testid="booking-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[var(--color-ink)]">Nome dell'animale *</Label>
                <Input value={form.pet_name} onChange={e => setForm(f => ({...f, pet_name: e.target.value}))} placeholder="Es. Fido" className="mt-1 border-[var(--color-border)]" data-testid="booking-pet-name" />
              </div>
              <div>
                <Label className="text-[var(--color-ink)]">Specie</Label>
                <Select value={form.pet_species} onValueChange={v => setForm(f => ({...f, pet_species: v}))}>
                  <SelectTrigger className="mt-1 border-[var(--color-border)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cane">Cane</SelectItem>
                    <SelectItem value="gatto">Gatto</SelectItem>
                    <SelectItem value="coniglio">Coniglio</SelectItem>
                    <SelectItem value="uccello">Uccello</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[var(--color-ink)]">Il tuo nome *</Label>
                <Input value={form.contact_name} onChange={e => setForm(f => ({...f, contact_name: e.target.value}))} placeholder="Nome e Cognome" className="mt-1 border-[var(--color-border)]" data-testid="booking-contact-name" />
              </div>
              <div>
                <Label className="text-[var(--color-ink)]">Email *</Label>
                <Input type="email" value={form.contact_email} onChange={e => setForm(f => ({...f, contact_email: e.target.value}))} placeholder="email@esempio.it" className="mt-1 border-[var(--color-border)]" data-testid="booking-contact-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[var(--color-ink)]">Telefono</Label>
                <Input value={form.contact_phone} onChange={e => setForm(f => ({...f, contact_phone: e.target.value}))} placeholder="+39 333 1234567" className="mt-1 border-[var(--color-border)]" />
              </div>
              <div>
                <Label className="text-[var(--color-ink)]">Data preferita</Label>
                <Input type="date" value={form.preferred_date} onChange={e => setForm(f => ({...f, preferred_date: e.target.value}))} className="mt-1 border-[var(--color-border)]" />
              </div>
            </div>
            <div>
              <Label className="text-[var(--color-ink)]">Indirizzo (per ritiro domiciliare)</Label>
              <Input value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} placeholder="Via, Numero, Città" className="mt-1 border-[var(--color-border)]" />
            </div>
            <div>
              <Label className="text-[var(--color-ink)]">Note aggiuntive</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Qualsiasi informazione utile..." className="mt-1 border-[var(--color-border)] resize-none" rows={3} />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl btn-press" data-testid="booking-submit-button">
              {submitting ? 'Invio in corso...' : 'Invia richiesta'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
